// Small procedural primitives for the toy-box look (§12.1: primitives, lathes, extrusions).
// Everything returns a fresh BufferGeometry with normals, ready for PartGeometryBuilder.add.
import {
  BufferGeometry, CatmullRomCurve3, Euler, ExtrudeGeometry, Float32BufferAttribute, LatheGeometry, Matrix4,
  Quaternion, Shape, SplineCurve, TubeGeometry, Vector2, Vector3, type Curve,
} from 'three';

const q = new Quaternion();
const e = new Euler();
const s = new Vector3();
const p = new Vector3();

/** Matrix from position, Euler rotation (radians, XYZ) and scale. */
export function M(px = 0, py = 0, pz = 0, rx = 0, ry = 0, rz = 0, sx = 1, sy = sx, sz = sx): Matrix4 {
  return new Matrix4().compose(p.set(px, py, pz), q.setFromEuler(e.set(rx, ry, rz)), s.set(sx, sy, sz));
}

/** A lathe through smoothed profile points (radius, y). */
export function smoothLathe(points: readonly [number, number][], steps: number, segments: number, phiStart = 0, phiLength = Math.PI * 2): LatheGeometry {
  const curve = new SplineCurve(points.map(([r, y]) => new Vector2(r, y)));
  const pts = curve.getPoints(steps).map((v) => new Vector2(Math.max(0, v.x), v.y));
  return new LatheGeometry(pts, segments, phiStart, phiLength);
}

/** A tube along `curve` whose radius tapers from r0 to r1 (TubeGeometry rings rescaled). */
export function taperedTube(curve: Curve<Vector3>, r0: number, r1: number, tubular: number, radial: number, closed = false): TubeGeometry {
  const g = new TubeGeometry(curve, tubular, 1, radial, closed);
  const pos = g.getAttribute('position');
  const c = new Vector3();
  const v = new Vector3();
  for (let i = 0; i <= tubular; i++) {
    const t = i / tubular;
    curve.getPointAt(t, c);
    const r = r0 + (r1 - r0) * t;
    for (let j = 0; j <= radial; j++) {
      const k = i * (radial + 1) + j;
      v.fromBufferAttribute(pos, k).sub(c).multiplyScalar(r).add(c);
      pos.setXYZ(k, v.x, v.y, v.z);
    }
  }
  pos.needsUpdate = true;
  return g;
}

/** A curve through points (Catmull-Rom). */
export function curve(points: readonly [number, number, number][], closed = false): CatmullRomCurve3 {
  return new CatmullRomCurve3(points.map(([x, y, z]) => new Vector3(x, y, z)), closed, 'centripetal');
}

/** Extrusion of a 2D outline (in the XY plane) along +Z by `depth`, centered on z, with a soft bevel. */
export function extrude(points: readonly [number, number][], depth: number, bevel = 0.012): ExtrudeGeometry {
  const shape = new Shape(points.map(([x, y]) => new Vector2(x, y)));
  const g = new ExtrudeGeometry(shape, { depth, bevelEnabled: bevel > 0, bevelSize: bevel, bevelThickness: bevel, bevelSegments: 2, curveSegments: 6 });
  g.translate(0, 0, -depth / 2);
  g.computeVertexNormals();
  return g;
}

/** Five-point star outline. */
export function starPoints(outer: number, inner: number, n = 5): [number, number][] {
  const out: [number, number][] = [];
  for (let i = 0; i < n * 2; i++) {
    const a = Math.PI / 2 + (i * Math.PI) / n;
    const r = i % 2 === 0 ? outer : inner;
    out.push([Math.cos(a) * r, Math.sin(a) * r]);
  }
  return out;
}

/**
 * A grid surface from a function (u, v) → point, u around (wrapping when `wrapU`), v across.
 * Normals come from computeVertexNormals; seams share no vertices so they stay hard.
 */
export function gridSurface(nu: number, nv: number, wrapU: boolean, fn: (u: number, v: number, out: Vector3) => void): BufferGeometry {
  const pos: number[] = [];
  const idx: number[] = [];
  const v3 = new Vector3();
  for (let j = 0; j <= nv; j++) {
    for (let i = 0; i <= nu; i++) {
      fn(i / nu, j / nv, v3);
      pos.push(v3.x, v3.y, v3.z);
    }
  }
  const row = nu + 1;
  for (let j = 0; j < nv; j++) {
    for (let i = 0; i < nu; i++) {
      const a = j * row + i;
      const b = a + row;
      idx.push(a, b, a + 1, b, b + 1, a + 1);
    }
  }
  const g = new BufferGeometry();
  g.setAttribute('position', new Float32BufferAttribute(pos, 3));
  g.setIndex(idx);
  g.computeVertexNormals();
  if (wrapU) {
    // Average the seam normals so wrapped surfaces shade smoothly across u = 0/1.
    const n = g.getAttribute('normal');
    const a = new Vector3();
    const b = new Vector3();
    for (let j = 0; j <= nv; j++) {
      const i0 = j * row;
      const i1 = j * row + nu;
      a.fromBufferAttribute(n, i0);
      b.fromBufferAttribute(n, i1);
      a.add(b).normalize();
      n.setXYZ(i0, a.x, a.y, a.z);
      n.setXYZ(i1, a.x, a.y, a.z);
    }
  }
  return g;
}

/**
 * A closed band (a ring wall with thickness) whose top edge follows `top(angle)`: paper crowns,
 * zigzag bands. Radius r, wall thickness t, bottom at y = 0.
 */
export function band(r: number, t: number, segments: number, top: (a: number) => number): BufferGeometry {
  // Ring loop: outer wall up, top strip in, inner wall down, bottom strip out (v = 0..4).
  return gridSurface(segments, 4, true, (u, v, out) => {
    const a = u * Math.PI * 2;
    const h = top(a);
    const k = Math.round(v * 4);
    const ro = r + t / 2;
    const ri = r - t / 2;
    const rr = k === 0 || k === 1 || k === 4 ? ro : ri;
    const y = k === 1 || k === 2 ? h : 0;
    out.set(Math.sin(a) * rr, y, Math.cos(a) * rr);
  });
}

/** An annulus "brim" from radius r0 to r1 with thickness, lifted by `lift(angle, t01)`. */
export function brim(r0: number, r1: number, thick: number, segments: number, lift: (a: number, t: number) => number, rScale: (a: number) => number = () => 1): BufferGeometry {
  // Loop around the cross-section: top surface outward, rim down, bottom inward, inner rim up.
  const nv = 8;
  return gridSurface(segments, nv, true, (u, v, out) => {
    const a = u * Math.PI * 2;
    const k = v * nv;
    let t: number;
    let dy: number;
    if (k <= 3) {
      t = k / 3;
      dy = thick / 2;
    } else if (k <= 4) {
      t = 1;
      dy = thick / 2 - (k - 3) * thick;
    } else if (k <= 7) {
      t = 1 - (k - 4) / 3;
      dy = -thick / 2;
    } else {
      t = 0;
      dy = -thick / 2 + (k - 7) * thick;
    }
    const rr = (r0 + (r1 - r0) * t) * (t > 0 ? rScale(a) : 1);
    out.set(Math.sin(a) * rr, lift(a, t) + dy, Math.cos(a) * rr);
  });
}

/** A helix spring along +Z from 0 to 1 (unit length), coil radius r, wire radius w. */
export function helixSpring(turns: number, r: number, w: number): TubeGeometry {
  const pts: Vector3[] = [];
  const n = turns * 12;
  for (let i = 0; i <= n; i++) {
    const t = i / n;
    const a = t * turns * Math.PI * 2;
    pts.push(new Vector3(Math.cos(a) * r, Math.sin(a) * r, t));
  }
  return new TubeGeometry(new CatmullRomCurve3(pts), n * 2, w, 6, false);
}

/** A teardrop (sweat) pointing up, radius r. */
export function teardrop(r: number, segments = 12): LatheGeometry {
  const pts: Vector2[] = [];
  for (let i = 0; i <= 10; i++) {
    const t = i / 10;
    const a = Math.PI * t;
    const rad = r * Math.sin(a) * (1 - 0.55 * t * t);
    pts.push(new Vector2(rad, -r * Math.cos(a) * 1.2 + t * t * r * 0.9));
  }
  return new LatheGeometry(pts, segments);
}
